/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */

import React, { forwardRef, memo, type Ref, useRef, useState } from 'react';

import { css, jsx, styled } from '@compiled/react';
import { useMergeRefs } from 'use-callback-ref';

import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/migration/chevron-right';
import { ButtonItem, CustomItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	TAB_HEIGHT,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BACKGROUND_NEUTRAL_HOVERED_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_SUBTLE_VAR,
	UNSAFE_INTERNAL_MORE_COLOR_VAR,
} from '@atlassian/navigation-kit-common';
import type { DropdownTriggerProps } from '@atlassian/navigation-kit-common/src/types';

import AfterElements from '../../common/ui/after-elements';
import AfterElementsMoreItem from '../../common/ui/after-elements-more-item';
import BeforeTab from '../../common/ui/before-tab';
import MenuContainerWrapper from '../../common/ui/menu-container-wrapper';
import MenuWrapper from '../../common/ui/menu-wrapper';
import { RenameMenuItemComponent } from '../../common/ui/rename-menu-item';
import TabRenameForm from '../../common/ui/tab-rename-form';
import TabWrapper from '../../common/ui/tab-wrapper';
import { useTabContext } from '../../common/utils/tab-context';
import { useDraggableTabs } from '../../controllers/draggable-tabs';

const HeadingWhenSelected = ({
	isSelected,
	children,
}: {
	children: React.ReactNode;
	isSelected: boolean;
}) => {
	/* eslint-disable-next-line @atlaskit/design-system/use-heading */
	return isSelected ? <HeadingWrapper>{children}</HeadingWrapper> : <>{children}</>;
};

export const DropdownTriggerTab = memo(
	forwardRef(
		(
			{
				elemAfter,
				elemBefore,
				handleDrag,
				isChevronHidden = false,
				isDraggable = false,
				isMoreMenu,
				isOpen = false,
				isRenaming,
				name,
				onClick,
				onRenameCancel,
				onRenameComplete,
				position,
				renderMenu,
				testId,
				...buttonPassthroughProps
			}: DropdownTriggerProps,
			outerRef: Ref<HTMLElement>,
		) => {
			const { id, isSelected, isOverflowing } = useTabContext();
			const elementRef = useRef(null);
			const mergedRefs = useMergeRefs([outerRef, elementRef]);

			const isActive = isOpen || isSelected;

			const [isMenuOpen, setIsMenuOpen] = useState(false);
			const { closestEdge, isActiveDropZone, dragState, isDragging } = useDraggableTabs({
				isDragEnabled: isDraggable,
				isDropEnabled: true,
				position,
				id,
				handleDrag,
				isSelected,
				isOverflowing,
				isMoreMenu,
				elementRef,
			});

			const hasMenu = renderMenu != null;

			const rightChevron = isChevronHidden ? undefined : (
				<ChevronRightIcon color="currentColor" spacing="spacious" label="" size="small" />
			);
			const downChevron = isChevronHidden ? undefined : (
				<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
			);

			if (isOverflowing) {
				return (
					<div css={overflowItemWrapperStyles}>
						<MenuContainerWrapper
							isOverflowing
							isOpen={isMenuOpen}
							isSelected={isActive}
							hasMenu={hasMenu}
							isRenaming={isRenaming}
						>
							{isRenaming ? (
								<CustomItem
									component={RenameMenuItemComponent}
									iconBefore={elemBefore}
									iconAfter={
										elemAfter ? (
											<Inline alignBlock="center">
												{elemAfter}
												{rightChevron}
											</Inline>
										) : (
											rightChevron
										)
									}
									ref={mergedRefs}
								>
									<div css={hasMenu && overflowNameWrapperStyles}>
										<TabRenameForm
											defaultValue={name}
											onCancel={onRenameCancel}
											onConfirm={onRenameComplete}
										/>
									</div>
									<AfterElementsMoreItem
										elemBefore={elemBefore}
										elemAfter={
											elemAfter ? (
												<Inline alignBlock="center">
													{elemAfter}
													{rightChevron}
												</Inline>
											) : (
												rightChevron
											)
										}
										closestEdge={closestEdge}
										dragState={dragState}
										name={name}
									/>
								</CustomItem>
							) : (
								<ButtonItem
									{...buttonPassthroughProps}
									ref={mergedRefs}
									onClick={onClick}
									testId={testId}
									aria-current={isSelected ? 'page' : undefined}
									iconBefore={elemBefore}
									iconAfter={
										elemAfter ? (
											<Inline alignBlock="center">
												{elemAfter}
												{rightChevron}
											</Inline>
										) : (
											rightChevron
										)
									}
									isSelected={isActive}
								>
									<div css={hasMenu && overflowNameWrapperStyles}>{name}</div>
									<AfterElementsMoreItem
										elemBefore={elemBefore}
										elemAfter={
											elemAfter ? (
												<Inline alignBlock="center">
													{elemAfter}
													{rightChevron}
												</Inline>
											) : (
												rightChevron
											)
										}
										closestEdge={closestEdge}
										dragState={dragState}
										name={name}
									/>
								</ButtonItem>
							)}
							{hasMenu && (
								<MenuWrapper>
									<div css={actionItemsWrapperStyles}>
										{renderMenu != null &&
											renderMenu({
												isOverflowing,
												isOpen: isMenuOpen,
												setIsOpen: setIsMenuOpen,
											})}
									</div>
								</MenuWrapper>
							)}
						</MenuContainerWrapper>
					</div>
				);
			}

			return (
				<MenuContainerWrapper
					isSelected={isActive}
					isOpen={isMenuOpen}
					hasMenu={hasMenu}
					isRenaming={isRenaming}
				>
					<TabWrapper
						isSelected={isActive}
						isDragging={isDragging}
						ref={mergedRefs as Ref<HTMLDivElement>}
					>
						<div
							css={[boxStyles, isActiveDropZone && boxHighlightedStyles]}
							data-testid="navigation-kit-ui-tab.ui.dropdown-trigger-tab.tab-button"
						>
							{isRenaming ? (
								<div
									data-delegated-focus-ring
									data-testid={testId}
									css={[
										tabStyles,
										isMoreMenu && moreButtonBadgeStyles,
										isSelected && currentTabLabelStyles,
										fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles,
									]}
								>
									<BeforeTab elementBefore={elemBefore} />
									<TabRenameForm
										defaultValue={name}
										onCancel={onRenameCancel}
										onConfirm={onRenameComplete}
									/>
									<AfterElements
										elemAfter={
											elemAfter ? (
												<Inline alignBlock="center">
													{elemAfter}
													{downChevron}
												</Inline>
											) : (
												downChevron
											)
										}
										closestEdge={closestEdge}
										dragState={dragState}
										name={name}
									/>
								</div>
							) : (
								<HeadingWhenSelected isSelected={isSelected}>
									{/* eslint-disable-next-line @atlaskit/design-system/no-html-button */}
									<button
										{...buttonPassthroughProps}
										data-delegated-focus-ring
										data-testid={testId}
										type="button"
										onClick={onClick}
										css={[
											tabStyles,
											isMoreMenu && moreButtonBadgeStyles,
											isSelected && currentTabLabelStyles,
											fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles,
										]}
										aria-current={isSelected ? 'page' : undefined}
									>
										<BeforeTab elementBefore={elemBefore} />
										{name}
										<AfterElements
											elemAfter={
												elemAfter ? (
													<Inline alignBlock="center">
														{elemAfter}
														{downChevron}
													</Inline>
												) : (
													downChevron
												)
											}
											closestEdge={closestEdge}
											dragState={dragState}
											name={name}
										/>
									</button>
								</HeadingWhenSelected>
							)}
						</div>
						{hasMenu && (
							<MenuWrapper>
								{renderMenu != null && renderMenu({ isOpen: isMenuOpen, setIsOpen: setIsMenuOpen })}
							</MenuWrapper>
						)}
					</TabWrapper>
				</MenuContainerWrapper>
			);
		},
	),
);

const boxStyles = css({
	minWidth: 'max-content',
});

const boxHighlightedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BACKGROUND_NEUTRAL_HOVERED_VAR}, ${token('color.background.neutral.hovered')})`,
});

const currentTabLabelStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const HeadingWrapper = styled.h2({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'unset',
});

const tabStyles = css({
	display: 'flex',
	position: 'relative',
	paddingBlockStart: token('space.200'),
	paddingInlineEnd: token('space.0'),
	paddingBlockEnd: token('space.200'),
	paddingInlineStart: token('space.050'),
	margin: 0,
	textDecoration: 'none',
	fontWeight: token('font.weight.medium'),
	// formerly fontSize from @atlassian/jira-common-styles"
	// can't use the token becuase it sets things we don't want
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: `14px`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	letterSpacing: 'normal',
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: `${TAB_HEIGHT}px`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'unset',
	boxSizing: 'border-box',
	minWidth: 'max-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_SUBTLE_VAR}, ${token('color.text.subtle')})`,
	alignItems: 'center',
	gap: token('space.050', '4px'),
	border: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	'&:hover, &:focus-visible, &:focus, &:active': {
		boxShadow: 'none',
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	},
	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR}, ${token('color.link.pressed')})`,
		'&::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR}, ${token('color.link.pressed')})`,
		},
	},
	'&:focus, &:focus-visible': {
		outline: 'none',
	},
});

const activeAreaPaddingStyles = css({
	// Adding paddingLeft from TabWrapper to existing paddingInlineEnd to prevent gap between active area and tab
	paddingInlineEnd: token('space.075'),
	paddingInlineStart: `calc(${token('space.075')} + ${token('space.050')})`,
});

const overflowItemWrapperStyles = css({
	position: 'relative',
});

const overflowNameWrapperStyles = css({
	// These are there to ensure there is space for the menu. The 12px represents the gap between the name and menu,
	// which matches the gap for an icon after from the menu item styles, and the 24px is the width of the icon
	paddingRight: token('space.150', '12px'),
	marginRight: token('space.300', '24px'),
});

const actionItemsWrapperStyles = css({
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	right: token('space.500', '40px'),
	top: token('space.100', '8px'),
	gap: token('space.050', '4px'),
});

/**
 * Although these variables are used in the Badge, they are defined here so they can change on tab
 * hover/focus/active states.
 */
const moreButtonBadgeStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	[UNSAFE_INTERNAL_MORE_COLOR_VAR]: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_SUBTLE_VAR}, ${token('color.text.subtle')})`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	'&:hover, &:focus-visible, &:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		[UNSAFE_INTERNAL_MORE_COLOR_VAR]: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
	},
	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		[UNSAFE_INTERNAL_MORE_COLOR_VAR]: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR}, ${token('color.link.pressed')})`,
	},
});

export default DropdownTriggerTab;
